import {BlockControls, InspectorControls} from '@wordpress/block-editor';
import {CONFIG} from '../../../globals/config';
import type {BlockEditProps} from '@wordpress/blocks';
import {type Attr, block} from './block';
import {PanelBody, Slot, TextControl} from '@wordpress/components';
import type {FunctionComponent} from 'react';
import Accordion from '../Accordion';
import WidgetStyles from '../WidgetStyles';
import Menu from './Menu';
import Display from './Display';
import {__} from '@wordpress/i18n';
import {isScreen} from '../../../globals/helpers';
import {sanitize} from 'dompurify';

import styles from './edit.pcss';

export type FillProps = Pick<BlockEditProps<Attr>, 'clientId' | 'attributes' | 'setAttributes'>

export type SetAttributes = ( newValue: {
	[attribute in keyof Attr]?: Attr[attribute]
} ) => void;

type Props = BlockEditProps<Attr>;

const ErrorBoundary = window.ADVANCED_SIDEBAR_MENU.ErrorBoundary;

const Preview: FunctionComponent<{
	attributes: Attr;
	block: string;
	clientId: string;
}> = window.ADVANCED_SIDEBAR_MENU.Preview;

const Edit = ( {attributes, setAttributes, clientId, name}: Props ) => {
	// We have a version conflict or license error.
	if ( '' !== CONFIG.error ) {
		return ( <>
			<InspectorControls>
				<div
					className={styles.error}
					dangerouslySetInnerHTML={{__html: sanitize( CONFIG.error )}} />
			</InspectorControls>
			<Preview
				attributes={attributes}
				block={block.id}
				clientId={clientId} />
		</> );
	}

	const fillProps: FillProps = {
		attributes,
		setAttributes,
		clientId,
	};

	return ( <>
		<InspectorControls>
			<ErrorBoundary
				attributes={attributes}
				block={name}
				section={'navigation/Edit/general'}
			>
				{isScreen( [ 'widgets', 'site-editor', 'customize' ] ) && <PanelBody>
					<TextControl
						value={attributes.title ?? ''}
						label={__( 'Title', 'advanced-sidebar-menu' )}
						onChange={title => setAttributes( {title} )} />
				</PanelBody>}
				<PanelBody>
					<Menu attributes={attributes} setAttributes={setAttributes} />

					<Slot<FillProps>
						name="advanced-sidebar-menu/navigation/general"
						fillProps={fillProps} />

					<p>
						<a
							href={CONFIG.docs.navigation}
							target="_blank"
							rel="noopener noreferrer"
						>
							{__( 'block documentation', 'advanced-sidebar-menu' )}
						</a>
					</p>
				</PanelBody>

				<Display
					attributes={attributes}
					clientId={clientId}
					name={name}
					setAttributes={setAttributes} />

				<Accordion attributes={attributes} setAttributes={setAttributes} />
			</ErrorBoundary>

			<ErrorBoundary
				attributes={attributes}
				block={name}
				section={'navigation/Edit/inspector'}
			>
				<Slot<FillProps>
					name="advanced-sidebar-menu/navigation/inspector"
					fillProps={fillProps} />

			</ErrorBoundary>
		</InspectorControls>

		<BlockControls>
			<ErrorBoundary
				attributes={attributes}
				block={name}
				section={'navigation/Edit/block-controls'}
			>
				<WidgetStyles
					attributes={attributes}
					setAttributes={setAttributes} />

				<Slot<FillProps>
					name="advanced-sidebar-menu/navigation/block-controls"
					fillProps={fillProps} />

			</ErrorBoundary>
		</BlockControls>

		<ErrorBoundary
			attributes={attributes}
			block={name}
			section={'navigation/Edit/preview'}
		>
			<Preview attributes={attributes} block={block.id} clientId={clientId} />
		</ErrorBoundary>

	</> );
};

export default Edit;
